/**
 * Global Data Store - Single Source of Truth for Mock Data
 * 
 * This module ensures data consistency across the application by:
 * 1. Generating all mock data once and caching it
 * 2. Providing a single source of truth for all pages
 * 3. Ensuring Homepage metrics match detail screen data
 */

import { addDays, subDays, isToday, isWithinInterval } from 'date-fns';
import type { PromiseToPay, CollectionCase } from '@/types/collections';
import type { Receipt } from '@/types/payments';

// Mock dispute type for internal use (matches the shape used in generateMockDisputes)
interface MockDispute {
  id: string;
  customer: string;
  customerId: string;
  type: string;
  amount: number;
  status: string;
  priority: string;
  assignedTo: string;
  createdDate: Date;
  dueDate: Date;
  requiresResponse: boolean;
  workflow: string;
  aiScore: number;
  aiRecommendation: string;
  currentLevel: number;
  totalLevels: number;
  description: string;
  resolutionNotes: string;
}

// Activity type for case activities
interface CaseActivity {
  type: string;
  description: string;
  timestamp: Date;
  userId?: string;
}

// Import existing generators
import { generateMockPromises, generateMockCases } from './collections-mock-data';
import { generateMockReceipts } from '@/data/payments-mock-data';

/**
 * Global Data Store Singleton
 * Ensures all components use the same data
 */
class GlobalDataStore {
  // Reset instance to null to force regeneration with updated mock data
  private static instance: GlobalDataStore | null = null;
  
  // Cached data
  private promises: PromiseToPay[] = [];
  private cases: CollectionCase[] = [];
  private receipts: Receipt[] = [];
  private disputes: MockDispute[] = [];
  
  private constructor() {
    this.initializeData();
  }
  
  /**
   * Get singleton instance
   */
  public static getInstance(): GlobalDataStore {
    if (!GlobalDataStore.instance) {
      GlobalDataStore.instance = new GlobalDataStore();
    }
    return GlobalDataStore.instance;
  }
  
  /**
   * Reset the store (useful for testing or data refresh)
   */
  public static reset(): void {
    GlobalDataStore.instance = null;
  }
  
  /**
   * Initialize all mock data
   */
  private initializeData(): void {
    // Generate promises
    this.promises = generateMockPromises();
    
    // Generate cases
    this.cases = generateMockCases();
    
    // Generate receipts
    this.receipts = generateMockReceipts();
    
    // Generate disputes
    this.disputes = this.generateMockDisputes();
  }

  /**
   * Process promises to auto-update status based on promise date
   * Any promise with a past promise date and 'Active' status becomes 'Broken'
   */
  private processPromises(promises: PromiseToPay[]): PromiseToPay[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

    return promises.map(promise => {
      const promiseDate = new Date(promise.promiseDate);
      promiseDate.setHours(0, 0, 0, 0); // Reset time to start of day

      // If promise date is in the past and status is 'Active', mark as 'Broken'
      if (promiseDate < today && promise.status === 'Active') {
        const daysOverdue = Math.ceil((today.getTime() - promiseDate.getTime()) / (1000 * 60 * 60 * 24));
        return {
          ...promise,
          status: 'Broken' as const,
          daysOverdue,
        };
      }

      return promise;
    });
  }

  /**
   * Process a single promise to auto-update status based on promise date
   */
  private processPromise(promise: PromiseToPay): PromiseToPay {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const promiseDate = new Date(promise.promiseDate);
    promiseDate.setHours(0, 0, 0, 0);

    if (promiseDate < today && promise.status === 'Active') {
      const daysOverdue = Math.ceil((today.getTime() - promiseDate.getTime()) / (1000 * 60 * 60 * 24));
      return {
        ...promise,
        status: 'Broken' as const,
        daysOverdue,
      };
    }

    return promise;
  }

  /**
   * Process cases to auto-update their currentPromise status
   */
  private processCases(cases: CollectionCase[]): CollectionCase[] {
    return cases.map(caseItem => {
      if (caseItem.currentPromise) {
        const processedPromise = this.processPromise(caseItem.currentPromise);
        return {
          ...caseItem,
          currentPromise: processedPromise,
        };
      }
      return caseItem;
    });
  }
  
  /**
   * Generate mock disputes
   */
  private generateMockDisputes(): MockDispute[] {
    return [
      {
        id: "DSP-001",
        customer: "Acme Corporation",
        customerId: "1",
        type: "Billing Error",
        amount: 4250.00,
        status: "In Progress",
        priority: "High",
        assignedTo: "John Smith",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(), // Due today
        requiresResponse: true,
        workflow: "Standard Billing Dispute",
        aiScore: 85,
        aiRecommendation: "Approve partial credit of 30% based on similar cases",
        currentLevel: 2,
        totalLevels: 4,
        description: "Customer disputes billing amount for services rendered in December. Claims that agreed-upon discount was not applied to the invoice.",
        resolutionNotes: "",
      },
      {
        id: "DSP-002",
        customer: "TechStart Inc",
        customerId: "2",
        type: "Service Quality",
        amount: 10500.00,
        status: "Under Review",
        priority: "Critical",
        assignedTo: "Sarah Johnson",
        createdDate: subDays(new Date(), 2),
        dueDate: new Date(), // Due today
        requiresResponse: true,
        workflow: "Escalated Quality Dispute",
        aiScore: 72,
        aiRecommendation: "Escalate to senior management for review",
        currentLevel: 3,
        totalLevels: 4,
        description: "Major quality issues reported with delivered products. Customer requesting full refund and compensation.",
        resolutionNotes: "",
      },
      {
        id: "DSP-003",
        customer: "Global Solutions Ltd",
        customerId: "3",
        type: "Payment Dispute",
        amount: 7200.00,
        status: "New",
        priority: "Medium",
        assignedTo: "Mike Chen",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(), // Due today
        requiresResponse: true,
        workflow: "Standard Payment Dispute",
        aiScore: 91,
        aiRecommendation: "Verify payment records and contact customer",
        currentLevel: 1,
        totalLevels: 4,
        description: "Customer claims payment was already made but invoice shows outstanding balance.",
        resolutionNotes: "",
      },
      {
        id: "DSP-004",
        customer: "Enterprise Systems Co",
        customerId: "4",
        type: "Duplicate Charge",
        amount: 2650.00,
        status: "Resolved",
        priority: "Low",
        assignedTo: "Emily Davis",
        createdDate: subDays(new Date(), 10),
        dueDate: subDays(new Date(), 3),
        requiresResponse: false,
        workflow: "Quick Resolution",
        aiScore: 95,
        aiRecommendation: "Issue credit immediately",
        currentLevel: 4,
        totalLevels: 4,
        description: "Duplicate invoice sent to customer. Credit issued and confirmed.",
        resolutionNotes: "Credit memo issued. Customer confirmed receipt and satisfaction.",
      },
      {
        id: "DSP-005",
        customer: "Innovate Labs",
        customerId: "5",
        type: "Contract Terms",
        amount: 15750.00,
        status: "Escalated",
        priority: "High",
        assignedTo: "David Wilson",
        createdDate: subDays(new Date(), 5),
        dueDate: addDays(new Date(), 2),
        requiresResponse: false,
        workflow: "Legal Review Required",
        aiScore: 68,
        aiRecommendation: "Consult legal team for contract interpretation",
        currentLevel: 3,
        totalLevels: 4,
        description: "Dispute over contract terms and pricing structure. Requires legal review.",
        resolutionNotes: "",
      },
      {
        id: "DSP-006",
        customer: "Digital Dynamics",
        customerId: "6",
        type: "Billing Error",
        amount: 5500.00,
        status: "In Progress",
        priority: "Medium",
        assignedTo: "Sarah Johnson",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(), // Due today
        requiresResponse: true,
        workflow: "Standard Billing Dispute",
        aiScore: 88,
        aiRecommendation: "Review invoice and issue correction",
        currentLevel: 2,
        totalLevels: 4,
        description: "Customer disputes invoice total. Calculation error suspected.",
        resolutionNotes: "",
      },
      {
        id: "DSP-007",
        customer: "Retail Solutions",
        customerId: "7",
        type: "Service Quality",
        amount: 4200.00,
        status: "New",
        priority: "High",
        assignedTo: "John Smith",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Standard Quality Dispute",
        aiScore: 82,
        aiRecommendation: "Schedule customer call to discuss concerns",
        currentLevel: 1,
        totalLevels: 4,
        description: "Customer reports quality issues with recent delivery.",
        resolutionNotes: "",
      },
      {
        id: "DSP-008",
        customer: "Manufacturing Plus",
        customerId: "8",
        type: "Payment Dispute",
        amount: 7800.00,
        status: "In Progress",
        priority: "Medium",
        assignedTo: "Mike Chen",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Standard Payment Dispute",
        aiScore: 89,
        aiRecommendation: "Verify payment application",
        currentLevel: 2,
        totalLevels: 4,
        description: "Customer claims payment not properly applied to account.",
        resolutionNotes: "",
      },
      {
        id: "DSP-009",
        customer: "Tech Innovations",
        customerId: "9",
        type: "Billing Error",
        amount: 5600.00,
        status: "Under Review",
        priority: "High",
        assignedTo: "Emily Davis",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Standard Billing Dispute",
        aiScore: 86,
        aiRecommendation: "Review pricing agreement",
        currentLevel: 2,
        totalLevels: 4,
        description: "Pricing discrepancy on recent invoice.",
        resolutionNotes: "",
      },
      {
        id: "DSP-010",
        customer: "Global Traders",
        customerId: "10",
        type: "Contract Terms",
        amount: 9200.00,
        status: "New",
        priority: "Critical",
        assignedTo: "David Wilson",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Legal Review Required",
        aiScore: 75,
        aiRecommendation: "Escalate to legal team",
        currentLevel: 1,
        totalLevels: 4,
        description: "Contract interpretation dispute.",
        resolutionNotes: "",
      },
      {
        id: "DSP-011",
        customer: "Premier Services",
        customerId: "11",
        type: "Duplicate Charge",
        amount: 3800.00,
        status: "In Progress",
        priority: "Medium",
        assignedTo: "Sarah Johnson",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Quick Resolution",
        aiScore: 93,
        aiRecommendation: "Issue credit immediately",
        currentLevel: 2,
        totalLevels: 4,
        description: "Duplicate invoice identified by customer.",
        resolutionNotes: "",
      },
      {
        id: "DSP-012",
        customer: "Logistics Express",
        customerId: "12",
        type: "Service Quality",
        amount: 6400.00,
        status: "Under Review",
        priority: "High",
        assignedTo: "John Smith",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Escalated Quality Dispute",
        aiScore: 78,
        aiRecommendation: "Arrange site visit",
        currentLevel: 3,
        totalLevels: 4,
        description: "Service delivery issues reported.",
        resolutionNotes: "",
      },
      {
        id: "DSP-013",
        customer: "Construction Co",
        customerId: "13",
        type: "Billing Error",
        amount: 8900.00,
        status: "New",
        priority: "High",
        assignedTo: "Mike Chen",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Standard Billing Dispute",
        aiScore: 84,
        aiRecommendation: "Review billing calculations",
        currentLevel: 1,
        totalLevels: 4,
        description: "Billing calculation error suspected.",
        resolutionNotes: "",
      },
      {
        id: "DSP-014",
        customer: "Healthcare Systems",
        customerId: "14",
        type: "Payment Dispute",
        amount: 12300.00,
        status: "In Progress",
        priority: "Critical",
        assignedTo: "Emily Davis",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Standard Payment Dispute",
        aiScore: 81,
        aiRecommendation: "Verify payment records",
        currentLevel: 2,
        totalLevels: 4,
        description: "Payment application discrepancy.",
        resolutionNotes: "",
      },
      {
        id: "DSP-015",
        customer: "Education Partners",
        customerId: "15",
        type: "Contract Terms",
        amount: 7500.00,
        status: "Under Review",
        priority: "Medium",
        assignedTo: "David Wilson",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Legal Review Required",
        aiScore: 77,
        aiRecommendation: "Review contract terms",
        currentLevel: 2,
        totalLevels: 4,
        description: "Contract terms clarification needed.",
        resolutionNotes: "",
      },
      {
        id: "DSP-016",
        customer: "Financial Services Ltd",
        customerId: "16",
        type: "Billing Error",
        amount: 5900.00,
        status: "New",
        priority: "High",
        assignedTo: "Sarah Johnson",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Standard Billing Dispute",
        aiScore: 87,
        aiRecommendation: "Review invoice details",
        currentLevel: 1,
        totalLevels: 4,
        description: "Invoice discrepancy reported.",
        resolutionNotes: "",
      },
      {
        id: "DSP-017",
        customer: "Transport Solutions",
        customerId: "17",
        type: "Service Quality",
        amount: 4700.00,
        status: "In Progress",
        priority: "Medium",
        assignedTo: "John Smith",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Standard Quality Dispute",
        aiScore: 83,
        aiRecommendation: "Schedule quality review",
        currentLevel: 2,
        totalLevels: 4,
        description: "Service quality concerns raised.",
        resolutionNotes: "",
      },
      {
        id: "DSP-018",
        customer: "Energy Corp",
        customerId: "18",
        type: "Payment Dispute",
        amount: 10500.00,
        status: "Under Review",
        priority: "Critical",
        assignedTo: "Mike Chen",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Standard Payment Dispute",
        aiScore: 79,
        aiRecommendation: "Verify payment status",
        currentLevel: 3,
        totalLevels: 4,
        description: "Payment verification required.",
        resolutionNotes: "",
      },
      {
        id: "DSP-019",
        customer: "Hospitality Group",
        customerId: "19",
        type: "Duplicate Charge",
        amount: 3500.00,
        status: "New",
        priority: "Medium",
        assignedTo: "Emily Davis",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Quick Resolution",
        aiScore: 92,
        aiRecommendation: "Issue credit",
        currentLevel: 1,
        totalLevels: 4,
        description: "Duplicate charge identified.",
        resolutionNotes: "",
      },
      {
        id: "DSP-020",
        customer: "Media Networks",
        customerId: "20",
        type: "Billing Error",
        amount: 6800.00,
        status: "In Progress",
        priority: "High",
        assignedTo: "David Wilson",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Standard Billing Dispute",
        aiScore: 85,
        aiRecommendation: "Review billing records",
        currentLevel: 2,
        totalLevels: 4,
        description: "Billing error investigation.",
        resolutionNotes: "",
      },
      {
        id: "DSP-021",
        customer: "Consulting Partners",
        customerId: "21",
        type: "Contract Terms",
        amount: 11200.00,
        status: "Under Review",
        priority: "Critical",
        assignedTo: "Sarah Johnson",
        createdDate: subDays(new Date(), 1),
        dueDate: new Date(),
        requiresResponse: true,
        workflow: "Legal Review Required",
        aiScore: 74,
        aiRecommendation: "Legal consultation needed",
        currentLevel: 3,
        totalLevels: 4,
        description: "Contract dispute escalation.",
        resolutionNotes: "",
      },
    ];
  }
  
  // ============================================
  // PROMISES QUERIES
  // ============================================
  
  /**
   * Get all promises
   */
  public getAllPromises(): PromiseToPay[] {
    return this.processPromises(this.promises);
  }
  
  /**
   * Get promises due today
   */
        public getPromisesDueToday(): PromiseToPay[] {
          const processed = this.processPromises(this.promises);
          return processed.filter(p => {
      const promiseDate = new Date(p.promiseDate);
      return isToday(promiseDate) && p.status === 'Active';
    });
  }
  
  /**
   * Get promises due this week
   */
  public getPromisesDueThisWeek(): PromiseToPay[] {
    const today = new Date();
    const endOfWeek = addDays(today, 7);
    const processed = this.processPromises(this.promises);
    return processed.filter(p => {
      const promiseDate = new Date(p.promiseDate);
      return isWithinInterval(promiseDate, { start: today, end: endOfWeek }) && p.status === 'Active';
    });
  }
  
  /**
   * Get broken promises
   */
  public getBrokenPromises(): PromiseToPay[] {
    const processed = this.processPromises(this.promises);
    return processed.filter(p => p.status === 'Broken');
  }
  
  /**
   * Get promises kept today
   */
  public getPromisesKeptToday(): PromiseToPay[] {
    return this.promises.filter(p => {
      // In a real system, this would check when the promise was fulfilled
      // For mock data, we'll return a subset of 'Paid' promises
      return p.status === 'Paid';
    }).slice(0, 25); // Return first 25 to ensure we have at least 20
  }
  
  // ============================================
  // CASES QUERIES
  // ============================================
  
  /**
   * Get all cases
   */
  public getAllCases(): CollectionCase[] {
    return this.processCases(this.cases);
  }
  
  /**
   * Get cases due today
   */
        public getCasesDueToday(): CollectionCase[] {
          const processed = this.processCases(this.cases);
          return processed.filter(c => {
      const actionDate = new Date(c.nextActionDate);
      return isToday(actionDate) && c.status === 'Active';
    });
  }
  
  /**
   * Get cases resolved today
   */
  public getCasesResolvedToday(): CollectionCase[] {
    // In a real system, this would check resolution date
    // For mock data, we'll return a subset of 'Closed' cases
    const processed = this.processCases(this.cases);
    return processed.filter(c => c.status === 'Closed').slice(0, 3);
  }
  
  // ============================================
  // RECEIPTS QUERIES
  // ============================================
  
  /**
   * Get all receipts
   */
  public getAllReceipts(): Receipt[] {
    return this.receipts;
  }
  
  /**
   * Get unallocated receipts
   */
  public getUnallocatedReceipts(): Receipt[] {
    return this.receipts.filter(r => r.allocationStatus === 'unallocated');
  }
  
  /**
   * Get receipts received today
   */
  public getReceiptsReceivedToday(): Receipt[] {
    return this.receipts.filter(r => {
      const receiptDate = new Date(r.receiptDate);
      return isToday(receiptDate);
    });
  }
  
  /**
   * Get partially allocated receipts
   */
  public getPartiallyAllocatedReceipts(): Receipt[] {
    return this.receipts.filter(r => r.allocationStatus === 'partially_allocated');
  }
  
  // ============================================
  // DISPUTES QUERIES
  // ============================================
  
  /**
   * Get all disputes
   */
  public getAllDisputes(): MockDispute[] {
    return this.disputes;
  }
  
  /**
   * Get disputes requiring response today
   */
        public getDisputesRequiringResponseToday(): MockDispute[] {
          return this.disputes.filter(d => {
      const dueDate = new Date(d.dueDate);
      return isToday(dueDate) && d.requiresResponse && d.status !== 'Resolved' && d.status !== 'Closed';
    });
  }
  
  /**
   * Get active disputes
   */
  public getActiveDisputes(): MockDispute[] {
    return this.disputes.filter(d => 
      d.status === 'In Progress' || 
      d.status === 'Under Review' || 
      d.status === 'New' ||
      d.status === 'Escalated'
    );
  }

  /**
   * Update a case with new data
   */
  public updateCase(caseId: string, updates: Partial<CollectionCase>): void {
    const caseIndex = this.cases.findIndex(c => c.caseId === caseId);
    if (caseIndex !== -1) {
      this.cases[caseIndex] = {
        ...this.cases[caseIndex],
        ...updates,
      };
    }
  }

  /**
   * Add an activity entry to a case
   */
  public addActivity(caseId: string, activity: CaseActivity): void {
    const caseIndex = this.cases.findIndex(c => c.caseId === caseId);
    if (caseIndex !== -1) {
      // In a real app, this would be stored in a separate activities collection
      // For now, we'll just log it
      console.log(`Activity added to case ${caseId}:`, activity);
    }
  }
}

// Export singleton instance getter
export const getGlobalDataStore = () => GlobalDataStore.getInstance();

// Export reset function for testing
export const resetGlobalDataStore = () => GlobalDataStore.reset();
